/**
 * Single Zustand store. Keeps the slice count small while we build —
 * split when the file passes ~300 lines.
 *
 * Persisted slices: cart, orders, invoices, freshness map. A cold
 * launch renders the last known data instantly while the network
 * refresh runs in the background — same TTL caching pattern the
 * mobile app uses.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { Diamond, CartItem, Order, UserClaims, PaymentTerms } from './../api/types'

interface AppState {
  user: UserClaims | null
  terms: PaymentTerms | null
  signedIn: boolean
  setSignedIn: (v: boolean) => void
  setUser: (u: UserClaims | null, t: PaymentTerms | null) => void

  cartItems: CartItem[]
  setCart: (items: CartItem[]) => void

  orders: Order[]
  setOrders: (orders: Order[]) => void
  invoices: Order[]
  setInvoices: (invoices: Order[]) => void

  searchResults: Diamond[]
  setSearchResults: (results: Diamond[]) => void

  // Per-resource freshness timestamps so a tab-back to /cart, /orders,
  // /invoices etc. renders cached state instantly and only refetches
  // when stale. Same pattern as mobile.
  freshness: Record<string, number>
  markFresh: (key: string) => void
  invalidate: (key: string) => void
  isFresh: (key: string, ttlMs: number) => boolean
}

export const useApp = create<AppState>()(
  persist(
    (set, get) => ({
      user: null,
      terms: null,
      signedIn: false,
      setSignedIn: (signedIn) => set({ signedIn }),
      setUser: (user, terms) => set({ user, terms }),

      cartItems: [],
      setCart: (cartItems) => set({ cartItems }),

      orders: [],
      setOrders: (orders) => set({ orders }),
      invoices: [],
      setInvoices: (invoices) => set({ invoices }),

      searchResults: [],
      setSearchResults: (searchResults) => set({ searchResults }),

      freshness: {},
      markFresh: (key: string) => set((s) => ({ freshness: { ...s.freshness, [key]: Date.now() } })),
      invalidate: (key: string) => set((s) => {
        if (!(key in s.freshness)) return {} as Partial<AppState>
        const next = { ...s.freshness }
        delete next[key]
        return { freshness: next }
      }),
      isFresh: (key: string, ttlMs: number): boolean => {
        const t = (get().freshness || {})[key]
        return typeof t === 'number' && Date.now() - t < ttlMs
      },
    }),
    {
      name: 'aska-platform-state',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        cartItems: s.cartItems,
        orders: (s.orders || []).slice(0, 50),
        invoices: (s.invoices || []).slice(0, 50),
        freshness: s.freshness || {},
      }),
    }
  )
)
